import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { PopoverSearchHeader } from "@/components/ui/popover-search-header";

export interface GatewayOption {
	sn: string;
	label?: string;
}

interface GatewaySearchComboboxProps {
	value: string;
	onChange: (value: string) => void;
	options: GatewayOption[];
	isLoading?: boolean;
	onSearch: (query: string) => void;
}

export function GatewaySearchCombobox({
	value,
	onChange,
	options,
	isLoading,
	onSearch,
}: GatewaySearchComboboxProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	function handleOpenChange(isOpen: boolean) {
		setOpen(isOpen);
		if (isOpen) {
			setTimeout(() => inputRef.current?.focus(), 0);
		} else {
			setQuery("");
			onSearch("");
		}
	}

	function handleQueryChange(val: string) {
		setQuery(val);
		onSearch(val);
	}

	function handleSelect(sn: string) {
		onChange(sn === value ? "" : sn);
		setOpen(false);
	}

	function handleClear(e: React.MouseEvent) {
		e.stopPropagation();
		onChange("");
	}

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="h-10 w-full justify-between sm:w-72 md:w-96"
				>
					<span className="flex items-center gap-2 truncate">
						<MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
						{value ? (
							<span className="font-mono text-sm">{value}</span>
						) : (
							<span className="text-muted-foreground">
								{t("mqttSources.searchPlaceholder")}
							</span>
						)}
					</span>
					<span className="flex items-center gap-1">
						{value && (
							<X
								className="size-3.5 text-muted-foreground hover:text-foreground"
								onClick={handleClear}
							/>
						)}
						<ChevronsUpDown className="size-4 text-muted-foreground" />
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[var(--radix-popover-trigger-width)] p-0"
				align="start"
			>
				<PopoverSearchHeader
					ref={inputRef}
					placeholder={t("mqttSources.searchPlaceholder")}
					value={query}
					onValueChange={handleQueryChange}
				/>
				<div className="max-h-60 overflow-y-auto p-1">
					{isLoading ? (
						<div className="px-3 py-4 text-center text-sm text-muted-foreground">
							{t("meterTable.loading", "Loading…")}
						</div>
					) : options.length === 0 ? (
						<div className="px-3 py-4 text-center text-sm text-muted-foreground">
							{t("meterTable.noData")}
						</div>
					) : (
						options
							.filter(
								(opt) =>
									!query ||
									opt.sn.toLowerCase().includes(query.toLowerCase()) ||
									opt.label?.toLowerCase().includes(query.toLowerCase()),
							)
							.map((opt) => (
								<button
									key={opt.sn}
									type="button"
									onClick={() => handleSelect(opt.sn)}
									className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
								>
									<Check
										className={`size-4 shrink-0 ${value === opt.sn ? "opacity-100" : "opacity-0"}`}
									/>
									<span className="font-mono">{opt.sn}</span>
									{opt.label && (
										<span className="ml-auto truncate text-xs text-muted-foreground">
											{opt.label}
										</span>
									)}
								</button>
							))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
